import { useState, useEffect, useContext } from "react";
import "./App.css";
import DiscussionSearch from "./DiscussionSearch";
import NegotiationSearch from "./NegotiationSearch";
import { KeycloakProvider } from "./context";
import { lockbitClientIds } from "./utils";
import { API_BASE_URL } from "./api";
import { timestampToString } from "./utils";
import { compute_source_classname } from "./utils";
import { KeycloakContext } from "./context";
import { fetchTopicsFromApi } from "./api";
import ClipLoader from "react-spinners/ClipLoader";

function App() {
  const { authenticated, login, logout, keycloak, userInfos, keycloakLoading } = useContext(KeycloakContext);
  const [activeTab, setActiveTab] = useState("topics");
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ user: "", msg: "", title: "" });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [lockbitClient, setLockbitClient] = useState(24);

  const fetchTopics = async (pageToLoad = 1) => {
    setLoading(true);

    try {
      const data = await fetchTopicsFromApi(pageToLoad, filters, userInfos.apiKey);

      const merged =
        pageToLoad === 1 ? data.results : [...topics, ...data.results];

      const sorted = merged.sort((a, b) => {
        const dateA = a.messages?.[0]?.date || 0;
        const dateB = b.messages?.[0]?.date || 0;
        return dateB - dateA;
      });

      setTopics(sorted);
      setTotal(data.total);
      setHasMore(data.results.length > 0);
    } catch (e) {
      console.error("Erreur API :", e);
    } finally {
      setLoading(false);
    }
  };


  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchTopics(1);
  };

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (activeTab === "topics") fetchTopics(1);
  }, [activeTab]);

  useEffect(() => {
    const checkScrollbar = () => {
      const hasScrollbar = document.body.scrollHeight > window.innerHeight;
      document.body.style.paddingRight = hasScrollbar ? "0px" : "15px";
    };

    checkScrollbar(); // au montage

    window.addEventListener("resize", checkScrollbar);
    return () => window.removeEventListener("resize", checkScrollbar);
  }, [document.body.scrollHeight]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 100 >=
        document.documentElement.offsetHeight &&
        !loading &&
        hasMore &&
        activeTab === "topics"
      ) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchTopics(nextPage);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading, page, hasMore, filters, activeTab]);

  return (
    <div className="container">
      <div className="logo-container">
        <img className="logo" src="/logo.png" alt="Logo" />
      </div>
      {!authenticated ? (
        <div className="centred">
          {
            keycloakLoading ?
              <ClipLoader
                loading={keycloakLoading}
                size={25}
              /> :
              <button onClick={login}>Login</button>
          }
        </div>
      ) : (
        <>
          <div className="tabs">
            <button
              className={activeTab === "topics" ? "tab active" : "tab"}
              onClick={() => setActiveTab("topics")}
            >
              Search Topics
            </button>
            <button
              className={activeTab === "blackbasta" ? "tab active" : "tab"}
              onClick={() => setActiveTab("blackbasta")}
            >
              Blackbasta Leak
            </button>
            <button
              className={activeTab === "lockbit" ? "tab active" : "tab"}
              onClick={() => setActiveTab("lockbit")}
            >
              Lockbit Negotiations Leaks
            </button>
          </div>

          {activeTab === "topics" && (
            <>
              <h1>Search Topics</h1>
              <form onSubmit={handleSearch}>
                <input
                  name="user"
                  placeholder="By user"
                  value={filters.user}
                  onChange={handleChange}
                />
                <input
                  name="msg"
                  placeholder="Keyword in message"
                  value={filters.msg}
                  onChange={handleChange}
                />
                <input
                  name="title"
                  placeholder="Keyword in title"
                  value={filters.title}
                  onChange={handleChange}
                />
                <button type="submit">Search</button>
              </form>

              <p>
                <span className="totalcount">{total} topics found</span>
              </p>

              {loading && <p>Loading...</p>}

              {topics.length === 0 && !loading ? null : (
                topics.map((topic, idx) => (
                  <div key={idx} className="topic-card">
                    <h1>{topic.title}</h1>
                    <p className="topic-meta">Section: {topic.section}</p>
                    <p className="topic-meta">
                      Date: {timestampToString(topic.messages[0].date)}
                    </p>
                    <p>
                      <span className="topic-meta">Source : </span>
                      <span
                        className={
                          "topic-meta " + compute_source_classname(topic.source)
                        }
                      >
                        {topic.source}
                      </span>
                    </p>

                    <div className="message">
                      {topic.messages.map((msg, i) => {
                        const parts = [];
                        const regex = /\*\*\s*(code|quote)\s*\*\*(.*?)\*\*\s*\/\1\s*\*\*/gs;
                        let lastIndex = 0;
                        const content = msg.content;
                        let match;

                        while ((match = regex.exec(content)) !== null) {
                          const [fullMatch, tag, inner] = match;
                          if (match.index > lastIndex) {
                            parts.push({
                              type: "text",
                              content: content.slice(lastIndex, match.index),
                            });
                          }
                          parts.push({
                            type: tag,
                            content: inner.trim(),
                          });
                          lastIndex = match.index + fullMatch.length;
                        }

                        if (lastIndex < content.length) {
                          parts.push({
                            type: "text",
                            content: content.slice(lastIndex),
                          });
                        }

                        return (
                          <div className="message-container" key={i}>
                            <strong>{msg.user}:</strong>
                            {parts.map((part, idx) => {
                              if (part.type === "text") {
                                return <p key={idx}>{part.content}</p>;
                              }
                              if (part.type === "code") {
                                return (
                                  <pre key={idx} className="code-block">
                                    {part.content}
                                  </pre>
                                );
                              }
                              if (part.type === "quote") {
                                return (
                                  <blockquote key={idx} className="quote-block">
                                    {part.content}
                                  </blockquote>
                                );
                              }
                              return null;
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {activeTab === "blackbasta" && (
            <>
              <h1>Blackbasta Leak Message Search</h1>
              <DiscussionSearch messages={[]} />
            </>
          )}

          {activeTab === "lockbit" && (
            <>
              <h1>Lockbit Negotiations Leak Search</h1>
              <select
                value={lockbitClient}
                onChange={(e) => setLockbitClient(e.target.value)}
                className="select-client"
              >
                <option value="">-- Select a negotiation --</option>
                {lockbitClientIds.map((id) => (
                  <option key={id} value={id}>
                    client{id}
                  </option>
                ))}
              </select>
              <NegotiationSearch id={lockbitClient} />
            </>
          )}
        </>
      )}
    </div>
  );

}

export default App;
